/**
 * MeteoPage — full page rendered at /categoria/meteo.
 *
 * Uses Open-Meteo through /api/weather (no API key, free tier). Shows:
 *   • Current: temp, feels-like, description, humidity, wind km/h, sunrise, sunset
 *   • Today: min/max
 *   • Forecast: today · tomorrow · day after (icon, description, rain %, max/min)
 *   • Fetched-at timestamp
 *
 * Boutique styling consistent with the rest of the guest UI.
 * On any transient error/no-data → discreet single-line message.
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Icons from "lucide-react";
import { Loader2 } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { weatherIconName, weatherLabel } from "@/lib/helpers";
import { fetchWeather } from "@/lib/weather";

function formatTime(iso, lang = "it") {
  if (!iso) return "—";
  const m = /T(\d{2}):(\d{2})/.exec(iso);
  if (m) return `${m[1]}:${m[2]}`;
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(lang === "de" ? "de-DE" : lang === "en" ? "en-GB" : "it-IT", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function formatFetchedAt(iso, lang = "it") {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(lang === "de" ? "de-DE" : lang === "en" ? "en-GB" : "it-IT", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Rome",
    });
  } catch {
    return "—";
  }
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <div className="w-9 h-9 rounded-xl bg-ivory-100 flex items-center justify-center shrink-0">
        <Icon strokeWidth={1.3} className="w-4 h-4 text-bronze-400" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold tracking-overline text-ink-400 uppercase">{label}</div>
        <div className="text-sm text-ink-900 truncate">{value}</div>
      </div>
    </div>
  );
}

function ForecastRow({ label, code, tmax, tmin, rain, lang }) {
  const IconCmp = Icons[weatherIconName(code)] || Icons.Cloud;
  return (
    <div className="flex items-center gap-3 py-3">
      <div className="w-11 h-11 rounded-full bg-bronze-50 flex items-center justify-center shrink-0">
        <IconCmp strokeWidth={1.4} className="w-5 h-5 text-bronze-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-ink-900 leading-tight">{label}</div>
        <div className="text-xs text-ink-500 truncate">{weatherLabel(code, lang)}</div>
      </div>
      {typeof rain === "number" && rain > 0 && (
        <div className="flex items-center gap-1 text-[11px] text-ink-400 tabular-nums" title="Prob. pioggia">
          <Icons.Droplets strokeWidth={1.4} className="w-3.5 h-3.5" /> {rain}%
        </div>
      )}
      <div className="text-right tabular-nums leading-tight w-[80px]">
        <span className="text-sm text-ink-900 font-semibold">{Math.round(tmax)}°</span>
        <span className="text-sm text-ink-400"> · {Math.round(tmin)}°</span>
      </div>
    </div>
  );
}

export default function MeteoPage() {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const lat = settings?.weather_lat ?? 45.4964;
    const lng = settings?.weather_lng ?? 10.6083;
    fetchWeather(lat, lng)
      .then((res) => {
        if (cancelled) return;
        setData(res || { error: "weather_unavailable" });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [settings?.weather_lat, settings?.weather_lng]);

  const cur = data?.current;
  const daily = data?.daily;
  const unavailable = !loading && (!data || data.error || !cur || !daily);
  const IconCmp = cur ? Icons[weatherIconName(cur.weather_code)] || Icons.Cloud : Icons.CloudSun;
  const dayLabels = [t("weather.today"), t("weather.tomorrow"), t("weather.day_after")];

  return (
    <div className="px-5 pt-4" data-testid="meteo-page">
      {/* Header — matches other section pages */}
      <div className="text-center pt-4 animate-fade-up">
        <div className="mx-auto w-12 h-12 rounded-2xl bg-bronze-50 flex items-center justify-center">
          <IconCmp strokeWidth={1.4} className="w-5 h-5 text-bronze-500" />
        </div>
        <h1
          data-testid="meteo-title"
          className="font-editorial italic text-[38px] text-ink-900 mt-3 leading-tight"
        >
          {t("weather.title")}
        </h1>
        <p
          data-testid="meteo-tagline"
          className="font-editorial italic text-[15px] text-ink-500 mt-2"
        >
          {t("tagline.meteo")}
        </p>
        <div className="flex justify-center my-3">
          <span className="ornament" />
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-ink-300" data-testid="meteo-loading">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}

      {unavailable && !loading && (
        <p
          data-testid="meteo-unavailable"
          className="text-center text-sm text-ink-400 py-16 max-w-xs mx-auto leading-relaxed"
        >
          {t("weather.unavailable")}
        </p>
      )}

      {!loading && !unavailable && (
        <div className="animate-fade-up" style={{ animationDelay: "60ms" }}>
          {/* CURRENT — big card */}
          <section
            data-testid="meteo-current"
            className="rounded-2xl bg-gradient-to-br from-white to-ivory-100 border border-ink-200/40 shadow-soft px-5 py-5"
          >
            <div className="text-[10px] font-semibold tracking-overline text-bronze-500 uppercase">
              {t("weather.now")}
            </div>
            <div className="flex items-end justify-between gap-4 mt-1">
              <div className="min-w-0">
                <div className="flex items-baseline gap-3">
                  <span
                    className="font-editorial italic text-[64px] text-ink-900 leading-none tabular-nums"
                    data-testid="meteo-current-temp"
                  >
                    {Math.round(cur.temperature_2m)}°
                  </span>
                </div>
                <div className="text-sm text-ink-700 mt-1">
                  {weatherLabel(cur.weather_code, i18n.language)}
                </div>
                <div className="text-[12px] text-ink-400 mt-0.5">
                  {t("weather.feels_like")} {Math.round(cur.apparent_temperature)}°
                </div>
              </div>
              <div className="text-right tabular-nums shrink-0" data-testid="meteo-today-minmax">
                <div className="text-[10px] font-semibold tracking-overline text-ink-400 uppercase">
                  {t("weather.today")}
                </div>
                <div className="text-sm mt-1">
                  <span className="text-ink-900 font-semibold">{Math.round(daily.temperature_2m_max[0])}°</span>
                  <span className="text-ink-400"> · {Math.round(daily.temperature_2m_min[0])}°</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-5 pt-4 border-t border-ink-200/40">
              <Stat
                icon={Icons.Droplets}
                label={t("weather.humidity")}
                value={`${Math.round(cur.relative_humidity_2m)}%`}
              />
              <Stat
                icon={Icons.Wind}
                label={t("weather.wind")}
                value={`${Math.round(cur.wind_speed_10m)} km/h`}
              />
              <Stat
                icon={Icons.Sunrise}
                label={t("weather.sunrise")}
                value={formatTime(daily?.sunrise?.[0], i18n.language)}
              />
              <Stat
                icon={Icons.Sunset}
                label={t("weather.sunset")}
                value={formatTime(daily?.sunset?.[0], i18n.language)}
              />
            </div>
          </section>

          {/* FORECAST 3 days */}
          <section className="mt-5">
            <div className="text-[10px] font-semibold tracking-overline text-bronze-500 uppercase mb-1 px-1">
              {t("weather.forecast")}
            </div>
            <div
              className="rounded-2xl bg-white border border-ink-200/40 shadow-soft divide-y divide-ink-200/40 px-4"
              data-testid="meteo-forecast"
            >
              {(daily?.time || []).slice(0, 3).map((_, idx) => (
                <ForecastRow
                  key={idx}
                  label={dayLabels[idx]}
                  code={daily.weather_code[idx]}
                  tmax={daily.temperature_2m_max[idx]}
                  tmin={daily.temperature_2m_min[idx]}
                  rain={daily.precipitation_probability_max?.[idx]}
                  lang={i18n.language}
                />
              ))}
            </div>
          </section>

          {data?.fetched_at && (
            <p
              className="text-center text-[11px] text-ink-300 mt-4 mb-6"
              data-testid="meteo-updated"
            >
              {t("weather.updated_at", { time: formatFetchedAt(data.fetched_at, i18n.language) })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
